import { Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { ReactNode } from "react";
import { useTranslation } from "@localization/useTranslation";
import RiskReportChartPanel from "./RiskReportChartPanel";
import RisksOverAppetiteByAreaChart from "./risksOverAppetiteByAreaChart/RisksOverAppetiteByAreaChart";
import CountAboveAppetiteChart from "./countAboveAppetiteChart/CountAboveAppetiteChart";
import { CountAboveAppetitePoint, RisksOverAppetiteByAreaItem } from "./RiskManagementReport.types";

interface RiskReportChartsProps {
    stacked?: boolean;
    isLoading?: boolean;
    risksOverAppetiteByArea: Array<RisksOverAppetiteByAreaItem>;
    countAboveAppetite: Array<CountAboveAppetitePoint>;
    appetiteLimit: number;
}

function RiskReportCharts({ stacked = false, isLoading = false, risksOverAppetiteByArea, countAboveAppetite, appetiteLimit }: RiskReportChartsProps) {
    const { translateString } = useTranslation();

    const panels: Array<{ id: string; content: ReactNode }> = [
        {
            id: "risksOverAppetiteByArea",
            content: (
                <RiskReportChartPanel header={translateString("riskManagementReportRisksOverAppetiteByArea")}>
                    <RisksOverAppetiteByAreaChart isLoading={isLoading} areas={risksOverAppetiteByArea} />
                </RiskReportChartPanel>
            ),
        },
        {
            id: "countAboveAppetite",
            content: (
                <RiskReportChartPanel header={translateString("riskManagementReportCountAboveAppetite")}>
                    <CountAboveAppetiteChart isLoading={isLoading} points={countAboveAppetite} appetiteLimit={appetiteLimit} />
                </RiskReportChartPanel>
            ),
        },
    ];

    // In the PDF the panels are stacked as plain blocks so the print engine can keep each panel whole
    // (avoid-page-break) and pack as many as fit per page — page-break control is unreliable inside a flex Grid.
    // The spacing sits in top padding (not bottom margin) because padding survives at the top of a page break,
    // so a panel that flows onto a new page keeps a consistent margin above it.
    if (stacked) {
        return (
            <>
                {panels.map((panel) => (
                    <Box key={panel.id} className="avoid-page-break" sx={{ pt: 2 }}>
                        {panel.content}
                    </Box>
                ))}
            </>
        );
    }

    return (
        <Grid container spacing={2}>
            {panels.map((panel) => (
                <Grid key={panel.id} size={{ xs: 12, lg: 6 }}>
                    {panel.content}
                </Grid>
            ))}
        </Grid>
    );
}

export default RiskReportCharts;
